import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Monitor, Smartphone, ExternalLink, RefreshCw } from "lucide-react";

const PAGE_PREVIEW_URL = {
  homepage: "/",
  solutions: "/solutions",
  industries: "/industries",
  about: "/about",
  contact: "/contact",
  footer: "/",
  nav: "/",
  meta: "/",
};

function resolvePreviewUrl(pageKey) {
  if (PAGE_PREVIEW_URL[pageKey]) return PAGE_PREVIEW_URL[pageKey];
  if (pageKey?.startsWith("legal.")) {
    return `/legal/${pageKey.slice("legal.".length)}`;
  }
  return "/";
}

const DEVICES = {
  desktop: { label: "Desktop", w: "100%", Icon: Monitor },
  mobile: { label: "Mobile", w: "420px", Icon: Smartphone },
};

export default function PreviewModal({ open, pageKey, blocks, onClose }) {
  const iframeRef = useRef(null);
  const [device, setDevice] = useState("desktop");
  const [iframeReady, setIframeReady] = useState(false);
  const [nonce, setNonce] = useState(0);

  const url = resolvePreviewUrl(pageKey);

  // Send overrides whenever blocks or device changes while preview is open.
  useEffect(() => {
    if (!open || !iframeReady || !iframeRef.current) return;
    const overrides = Object.fromEntries(blocks.map((b) => [b.key, b.value]));
    iframeRef.current.contentWindow?.postMessage(
      { type: "neptune:preview-overrides", overrides },
      window.location.origin,
    );
  }, [blocks, open, iframeReady, nonce]);

  // Listen for iframe "ready" handshake.
  useEffect(() => {
    if (!open) {
      setIframeReady(false);
      return;
    }
    const onMsg = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === "neptune:preview-ready") setIframeReady(true);
    };
    window.addEventListener("message", onMsg);
    return () => {
      window.removeEventListener("message", onMsg);
      // Best-effort cleanup; iframe is unmounting anyway, but keeps context tidy.
      try {
        iframeRef.current?.contentWindow?.postMessage(
          { type: "neptune:preview-clear" },
          window.location.origin,
        );
      } catch { /* ignore */ }
    };
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const reload = () => {
    setIframeReady(false);
    setNonce((n) => n + 1);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[95] bg-[#060608]/95 backdrop-blur-md flex flex-col"
          data-testid="preview-modal"
          role="dialog"
          aria-modal="true"
        >
          {/* Toolbar */}
          <div className="flex items-center justify-between gap-4 px-5 sm:px-7 h-14 border-b border-white/[0.06] bg-[#0a0a0a]">
            <div className="flex items-center gap-3 min-w-0">
              <span
                className="inline-flex items-center gap-2 rounded-full border border-[#00E5FF]/30 bg-[#00E5FF]/[0.06] px-2.5 py-1 text-[10.5px] uppercase tracking-[0.22em] text-[#00E5FF]"
                data-testid="preview-badge"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#00E5FF] shadow-[0_0_8px_rgba(0,229,255,0.8)]" />
                Preview · unsaved
              </span>
              <span className="text-[12px] text-zinc-500 truncate hidden sm:inline">
                {pageKey} → {url}
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              {/* Device toggle */}
              <div className="hidden sm:flex items-center rounded-full border border-white/[0.08] bg-white/[0.02] p-0.5">
                {Object.entries(DEVICES).map(([k, d]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setDevice(k)}
                    data-testid={`preview-device-${k}`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] transition-colors ${
                      device === k
                        ? "bg-white/[0.08] text-white"
                        : "text-zinc-400 hover:text-white"
                    }`}
                  >
                    <d.Icon className="h-3 w-3" />
                    {d.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={reload}
                data-testid="preview-reload"
                title="Reload preview"
                className="h-8 w-8 inline-flex items-center justify-center rounded-full text-zinc-400 hover:text-white hover:bg-white/[0.05] transition-colors"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                data-testid="preview-open-new"
                title="Open published page in new tab"
                className="h-8 w-8 inline-flex items-center justify-center rounded-full text-zinc-400 hover:text-white hover:bg-white/[0.05] transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <button
                type="button"
                onClick={onClose}
                data-testid="preview-close"
                className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-white/10 text-zinc-200 hover:text-white hover:bg-white/[0.06] transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Frame stage */}
          <div className="flex-1 overflow-hidden relative flex items-stretch justify-center p-0 sm:p-6">
            <motion.div
              animate={{ maxWidth: device === "mobile" ? DEVICES.mobile.w : "100%" }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative h-full w-full flex items-stretch justify-center"
              style={{ maxWidth: device === "mobile" ? DEVICES.mobile.w : "100%" }}
            >
              <div className="relative flex-1 rounded-none sm:rounded-2xl overflow-hidden border-0 sm:border border-white/[0.06] bg-[#0a0a0a] shadow-[0_30px_120px_-20px_rgba(0,85,255,0.25)]">
                <iframe
                  key={nonce}
                  ref={iframeRef}
                  title="Neptune preview"
                  src={url}
                  data-testid="preview-iframe"
                  className="block h-full w-full bg-[#0a0a0a]"
                  onLoad={() => {
                    // Push overrides right after load even if ready handshake is missed.
                    const overrides = Object.fromEntries(
                      blocks.map((b) => [b.key, b.value]),
                    );
                    iframeRef.current?.contentWindow?.postMessage(
                      { type: "neptune:preview-overrides", overrides },
                      window.location.origin,
                    );
                    setIframeReady(true);
                  }}
                />
                {!iframeReady && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                      Loading preview…
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
